import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Button, Form, Row, Col, Container } from "react-bootstrap";
import FormInput from "../../components/ui/FormInput";
import { memberBusinessDetailsFields, handleFileChange } from "../../utils/utils";
import axios from "axios";
import { API_BASE_URL } from "../../config";
import { useNavigate } from "react-router-dom";
import ErrorMessage from "../../components/shared/ErrorMessage";

const MemberBusinessDetails = ({
  memberId,
  memberBusinessDetailsData,
  setMemberBusinessDetailsData,
  handleNext,
  type,
  locked = false,
}) => {
  const navigate = useNavigate();
  const [isEditingRaw, setIsEditingRaw] = useState(type === "Edit");
  // `locked` wins over this component's own isEditing. Passing type="View" is
  // NOT enough: the Edit button below renders *because* the type is View, and
  // clicking it sets isEditing = true, which would reopen a disbursed
  // application. Defaults to false so the Customers flow is unaffected.
  const isEditing = locked ? false : isEditingRaw;
  const setIsEditing = (v) => { if (!locked) setIsEditingRaw(v); };
  const [errorMsg, setErrorMsg] = useState("");
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
    control,
  } = useForm({
    defaultValues: memberBusinessDetailsData,
  });

  const [filePreviews, setFilePreviews] = useState({});
  const [existingFiles, setExistingFiles] = useState({});
  const [fieldsData, setFieldsData] = useState([]);

  useEffect(() => {
    const fetchFieldsData = async () => {
      const data = await memberBusinessDetailsFields();
      setFieldsData(data);
    };
    fetchFieldsData();
  }, []);

  const businessCategoryId = watch("businessCategoryId");

  useEffect(() => {
    const fetchFieldsData = async () => {
      const data = await memberBusinessDetailsFields(businessCategoryId);
      setFieldsData(data);
    };
    if (businessCategoryId) {
      fetchFieldsData();
    }
  }, [businessCategoryId]);

  // Employee or Business — only one set of fields applies to a given customer.
  const occupationType = watch("occupationType");

  // A record saved before this dropdown existed. Such rows may hold Employee AND
  // Business data — 100 rows on jothilinga2 do — so neither set may be hidden or
  // cleared for them.
  //
  // Read from the LOADED record, never from watch(): derived from the live form
  // value this would flip the moment the operator picked a type, and the clear
  // below would fire on exactly the records it must not touch. The id half means
  // a brand-new record can never match — initialMemberBusinessDetails has no id.
  const isLegacyRecord = Boolean(
    memberBusinessDetailsData?.id && !memberBusinessDetailsData?.occupationType
  );

  // Untagged fields always show. On a legacy record everything shows, so no
  // saved value is ever invisible; otherwise the picked type decides, and before
  // a choice is made both sets show so the form asks the question first.
  const visibleFields = fieldsData.filter(
    (f) =>
      !f.occupation ||
      isLegacyRecord ||
      !occupationType ||
      f.occupation === occupationType
  );

  // Clear the set that just became hidden. A hidden input keeps whatever was
  // typed before the switch and would save it invisibly — a Business record
  // must not carry a stray Monthly Salary. Same rule as Spouse's Name.
  useEffect(() => {
    // Legacy records are exempt: picking a type on one must not discard the
    // other half of what is already stored. The chosen type still saves; the
    // record simply keeps everything it had.
    if (isLegacyRecord) return;
    // Also skipped before a choice is made, so loading never wipes anything.
    if (!occupationType) return;
    fieldsData
      .filter((f) => f.occupation && f.occupation !== occupationType)
      .forEach((f) => setValue(f.name, f.type === "checkbox" ? false : ""));
  }, [occupationType, fieldsData, setValue, isLegacyRecord]);

  useEffect(() => {
    if (memberBusinessDetailsData) {
      reset(memberBusinessDetailsData); // Reset form with new data
      Object.keys(memberBusinessDetailsData).forEach((key) => {
        setValue(key, memberBusinessDetailsData[key]);
        if (key.endsWith("Image") && memberBusinessDetailsData[key]) {
          setExistingFiles((prev) => ({
            ...prev,
            [key]: `${API_BASE_URL}/images/${memberBusinessDetailsData[key]}`,
          }));
        }
      });
    }
  }, [memberBusinessDetailsData, setValue, reset]);

  const onSubmit = async (submittedData) => {
    // Insert or update is decided by WHETHER THIS SECTION EXISTS, not by the
    // URL's `type`. That flag describes the loan; this tab is optional, so a
    // loan edited here may never have had a row at all.
    //
    // It used to send type:"Edit" whenever the loan was in Edit mode, taking the
    // id from a row that did not exist. `id: undefined` is dropped entirely by
    // JSON.stringify, so the server saw type:"Edit" with no id and refused with
    // "Update requires a valid ID." — 5,770 of 6,414 loans have no row here.
    const existingId = memberBusinessDetailsData?.id;
    const isNew = !existingId;

    // `id` out of the spread too: when the form was populated from a saved row it
    // carries one, which would otherwise contradict the decision made here.
    const { id: _ignoredId, ...fields } = submittedData;

    const payload = {
      ...fields,
      model: "member_business_details",
      type: isNew ? "Add" : "Edit",
      // Required, or the new row is orphaned with no loan.
      memberId,
    };
    if (!isNew) payload.id = existingId;

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/addDetails`,
        payload
      );

      if (response.status === 201) {
        setMemberBusinessDetailsData(response.data.savedData);
        console.log(
          "Member Business Details updated successfully: ",
          response.data
        );
        setIsEditing(false);

        if (type === "Add") {
          handleNext();
        }
        if (isEditing) {
          navigate("/addEditBusinessLoan", {
            state: { type: "View", stepNo: 4, memberId },
          });
        }
        setErrorMsg("");
      } else {
        setErrorMsg(response.data.error || "Error updating member business details");
      }
    } catch (error) {
      console.error("Error updating Member Business Details: ", error);
      const msg = error.response?.data?.error || error.response?.data?.message || error.message || "Error updating member business details";
      setErrorMsg(msg);
    }
  };

  const onError = (errors) => {
    const errorMessages = Object.keys(errors).reduce((acc, key) => {
      acc[key] = errors[key].message;
      return acc;
    }, {});
    console.log("Form errors:", errorMessages);
  };

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    reset(memberBusinessDetailsData); // Reset to original data
    setFilePreviews({}); // Clear any new file previews
  };

  return (
    <Container className="mt-4">
      <h2 className="text-center mb-4">
        Member Business Details
        {!locked && type === "View" && !isEditing && (
          <Button
            variant="primary"
            className="ms-3 mt-2 btn-hover"
            onClick={handleEditClick}
          >
            Edit
          </Button>
        )}
      </h2>
      <ErrorMessage message={errorMsg} />
      <Form onSubmit={handleSubmit(onSubmit, onError)}>
        <Row>
          {visibleFields.map((field, index) => (
            <Col
              xs={12}
              md={6}
              key={index}
              className="mb-3"
            >
              <FormInput
                label={field.label}
                type={field.type}
                name={field.name}
                register={register}
                watch={watch}
                control={control}
                errors={errors}
                options={field.options || []}
                readOnly={(type === "View" && !isEditing) || field.readOnly}
                handleFileChange={(e) =>
                  handleFileChange(e, field.name, setFilePreviews)
                }
                existingFileUrl={existingFiles[field.name] || ""}
                filePreview={filePreviews[field.name]?.previewUrl || ""}
                required={field.required}
                alphaOnly={field.alphaOnly}
              />
            </Col>
          ))}
        </Row>

        <div className="text-center mt-3">
          {(type !== "View" || isEditing) && (
            <Button variant="success" type="submit" className="me-2">
              {isEditing ? "Update" : "Submit"}
            </Button>
          )}
          {isEditing && (
            <Button
              variant="warning"
              onClick={handleCancelEdit}
              className="me-2"
            >
              Cancel
            </Button>
          )}
          {!locked && type === "View" && !isEditing && (
            <Button
              variant="secondary"
              onClick={() =>
                navigate("/addEditBusinessLoan", {
                  state: { type: "View", stepNo: 4, memberId },
                })
              }
            >
              Back
            </Button>
          )}
        </div>
      </Form>
    </Container>
  );
};

export default MemberBusinessDetails;